/**
 * SettingsPage — organisation and website scan settings.
 * Accessible under /websites/:websiteId/settings (inside WebsiteShell).
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { useFeatureReadiness } from "../hooks/use-feature-readiness.js";
import { apiPatch } from "../api/client.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { Skeleton } from "../components/skeleton.js";
import type {
  Organisation,
  OrgType,
  ScanDepth,
  ScanFrequency,
  Website,
} from "../api/types.js";

const ORG_TYPES: Array<{ value: OrgType; label: string }> = [
  { value: "public_body", label: "Public body / government" },
  { value: "private_company", label: "Private company" },
  { value: "non_profit", label: "Non-profit / charity" },
  { value: "educational", label: "Educational institution" },
  { value: "healthcare", label: "Healthcare provider" },
  { value: "other", label: "Other" },
];

const SCAN_DEPTHS: Array<{ value: ScanDepth; label: string }> = [
  { value: "10", label: "10 pages" },
  { value: "20", label: "20 pages" },
  { value: "50", label: "50 pages" },
  { value: "100", label: "100 pages" },
  { value: "150", label: "150 pages" },
  { value: "250", label: "250 pages" },
  { value: "500", label: "500 pages" },
  { value: "1000", label: "1,000 pages" },
];

const SCAN_FREQUENCIES: Array<{ value: ScanFrequency; label: string }> = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "manual", label: "Manual only" },
];

export function SettingsPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const qc = useQueryClient();
  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const website =
    websitesData?.websites.find((w) => w.id === websiteId) ?? null;
  const { data: billingData } = useBillingSummary(org?.id);
  const limits = billingData?.summary?.limits;
  const { data: features } = useFeatureReadiness();

  // Screenshots: plan says "included" but storage may not be ready.
  const screenshotsReady =
    limits?.screenshotsEnabled === true &&
    features?.screenshots?.status === "ready";

  // Org form state
  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState<OrgType>("other");
  const [orgCountry, setOrgCountry] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [scanEmailsEnabled, setScanEmailsEnabled] = useState(true);

  // Website form state
  const [scanDepth, setScanDepth] = useState<ScanDepth>("50");
  const [scanFrequency, setScanFrequency] = useState<ScanFrequency>("monthly");

  // Initialise org form when org data first arrives
  const orgId = org?.id;
  useEffect(() => {
    if (org) {
      setOrgName(org.name);
      setOrgType(org.type as OrgType);
      setOrgCountry(org.country);
      setOrgEmail(org.accessibilityContactEmail);
      setScanEmailsEnabled(org.scanEmailsEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Initialise website form when website data first arrives
  const websiteRowId = website?.id;
  useEffect(() => {
    if (website) {
      setScanDepth(website.scanDepth);
      setScanFrequency(website.scanFrequency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteRowId]);

  const orgMutation = useMutation({
    mutationFn: () =>
      apiPatch<{ org: Organisation }>(`/orgs/${org!.id}`, {
        name: orgName,
        type: orgType,
        country: orgCountry,
        accessibilityContactEmail: orgEmail,
        scanEmailsEnabled,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["orgs"] });
    },
  });

  const websiteMutation = useMutation({
    mutationFn: () =>
      apiPatch<{ website: Website }>(`/websites/${websiteId}/settings`, {
        scanDepth,
        scanFrequency,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["websites", org?.id] });
    },
  });

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (orgMutation.isSuccess) {
      const t = setTimeout(() => orgMutation.reset(), 3000);
      return () => clearTimeout(t);
    }
  }, [orgMutation.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (websiteMutation.isSuccess) {
      const t = setTimeout(() => websiteMutation.reset(), 3000);
      return () => clearTimeout(t);
    }
  }, [websiteMutation.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
          { label: "Settings" },
        ]}
      />
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">
        Manage your organisation and website scan settings.
      </p>

      {/* ── Organisation settings ─────────────────────────────────────────── */}
      <section
        className="product-section"
        aria-labelledby="org-settings-heading"
      >
        <h2 id="org-settings-heading" className="section-title">
          Organisation
        </h2>

        {!org ? (
          <Skeleton lines={3} />
        ) : (
          <form
            style={{ maxWidth: 520 }}
            onSubmit={(e) => {
              e.preventDefault();
              orgMutation.mutate();
            }}
          >
            <div className="form-field">
              <label htmlFor="org-name" className="form-field__label">
                Organisation name
              </label>
              <input
                id="org-name"
                type="text"
                className="form-field__input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                minLength={1}
              />
            </div>

            <div className="form-field">
              <label htmlFor="org-type" className="form-field__label">
                Type
              </label>
              <select
                id="org-type"
                className="form-field__select"
                value={orgType}
                onChange={(e) => setOrgType(e.target.value as OrgType)}
              >
                {ORG_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="org-country" className="form-field__label">
                Country
              </label>
              <input
                id="org-country"
                type="text"
                className="form-field__input"
                value={orgCountry}
                onChange={(e) => setOrgCountry(e.target.value)}
              />
            </div>

            <div className="form-field">
              <label htmlFor="org-email" className="form-field__label">
                Accessibility contact email
              </label>
              <input
                id="org-email"
                type="email"
                className="form-field__input"
                value={orgEmail}
                onChange={(e) => setOrgEmail(e.target.value)}
              />
            </div>

            <div className="form-field mt-3">
              <label
                htmlFor="scan-emails"
                className="form-field__label d-flex items-center gap-2 cursor-pointer"
              >
                <input
                  id="scan-emails"
                  type="checkbox"
                  checked={scanEmailsEnabled}
                  onChange={(e) => setScanEmailsEnabled(e.target.checked)}
                  style={{ width: "18px", height: "18px" }}
                />
                Send scan-result emails
              </label>
              <p className="text-muted text-sm" style={{ margin: "4px 0 0 26px" }}>
                When enabled, we send an email to the contact address above
                each time an accessibility scan finishes. This does not affect
                sign-in or billing emails.
              </p>
            </div>

            {orgMutation.isSuccess && (
              <p className="text-success text-sm my-2">
                Organisation settings saved.
              </p>
            )}
            {orgMutation.isError && (
              <div className="alert alert--error my-2">
                {orgMutation.error.message}
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary"
              disabled={orgMutation.isPending}
            >
              {orgMutation.isPending ? "Saving…" : "Save organisation"}
            </button>
          </form>
        )}
      </section>

      {/* ── Website scan settings ─────────────────────────────────────────── */}
      <section
        className="product-section"
        aria-labelledby="website-settings-heading"
      >
        <h2 id="website-settings-heading" className="section-title">
          Scan settings
        </h2>

        {!website ? (
          <Skeleton lines={3} />
        ) : (
          <form
            style={{ maxWidth: 520 }}
            onSubmit={(e) => {
              e.preventDefault();
              websiteMutation.mutate();
            }}
          >
            <div className="form-field">
              <label htmlFor="scan-depth" className="form-field__label">
                Scan depth
              </label>
              <p className="text-muted text-sm" style={{ margin: "0 0 4px" }}>
                Maximum representative pages the scanner will check per run.
                The actual number scanned may be lower due to template sampling.
              </p>
              <select
                id="scan-depth"
                className="form-field__select"
                value={scanDepth}
                onChange={(e) => setScanDepth(e.target.value as ScanDepth)}
              >
                {SCAN_DEPTHS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="scan-frequency" className="form-field__label">
                Scan frequency
              </label>
              <p className="text-muted text-sm" style={{ margin: "0 0 4px" }}>
                How often your site is automatically re-scanned.
              </p>
              <select
                id="scan-frequency"
                className="form-field__select"
                value={scanFrequency}
                onChange={(e) =>
                  setScanFrequency(e.target.value as ScanFrequency)
                }
              >
                {SCAN_FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            {websiteMutation.isSuccess && (
              <p className="text-success text-sm my-2">
                Scan settings saved.
              </p>
            )}
            {websiteMutation.isError && (
              <div className="alert alert--error my-2">
                {websiteMutation.error.message}
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary"
              disabled={websiteMutation.isPending}
            >
              {websiteMutation.isPending ? "Saving…" : "Save scan settings"}
            </button>
          </form>
        )}
      </section>

      {/* ── What your plan includes ────────────────────────────────────────── */}
      {limits && (
        <section
          className="product-section"
          aria-labelledby="plan-features-heading"
        >
          <h2 id="plan-features-heading" className="section-title">
            What your plan includes
          </h2>
          <table className="review-table">
            <tbody>
              <tr>
                <td>Plan</td>
                <td>{limits.planName}</td>
              </tr>
              <tr>
                <td>Max pages per scan</td>
                <td>{limits.maxPagesPerScan.toLocaleString()}</td>
              </tr>
              <tr>
                <td>Scans per month</td>
                <td>{limits.scansPerMonthPerWebsite}</td>
              </tr>
              <tr>
                <td>Visual evidence (screenshots)</td>
                <td>
                  {!limits.screenshotsEnabled
                    ? "Not included"
                    : screenshotsReady
                      ? "Included"
                      : "Included in plan, but storage not ready"}
                </td>
              </tr>
              <tr>
                <td>Document scanning</td>
                <td>{limits.docsEnabled ? "Included" : "Not included"}</td>
              </tr>
              <tr>
                <td>Recurring monitoring</td>
                <td>{limits.recurringEnabled ? "Included" : "Not included"}</td>
              </tr>
            </tbody>
          </table>
          {(!limits.screenshotsEnabled || !limits.docsEnabled || !limits.recurringEnabled) && (
            <p className="text-muted text-sm mt-2">
              <Link to="/billing">Upgrade your plan</Link> to unlock additional
              features.
            </p>
          )}
        </section>
      )}

      {/* ── How your scan works ────────────────────────────────────────────── */}
      <section
        className="product-section"
        aria-labelledby="how-scan-works-heading"
      >
        <h2 id="how-scan-works-heading" className="section-title">
          How your scan works
        </h2>
        <p className="text-sm mb-2">
          <strong>Tools:</strong> Axe-core, PA11y, and custom accessibility
          checks running in a headless Chromium browser.
        </p>
        <p className="text-sm mb-2">
          <strong>Page selection:</strong> The scanner discovers pages on your
          site, groups them by template (e.g. blog posts, product pages), and
          tests representative pages from each group. This gives broad coverage
          without scanning every individual page.
        </p>
        <p className="text-sm mb-2">
          <strong>What is not covered:</strong> Automated scans cannot assess
          content quality, keyboard-only workflows, screen reader compatibility,
          or cognitive accessibility. A manual review is required for full WCAG
          compliance.
        </p>
        <p className="text-sm text-muted">
          See your{" "}
          <Link to={`/websites/${websiteId}/report`}>latest report</Link> for
          detailed coverage and findings.
        </p>
      </section>

      {/* ── Scanner access ─────────────────────────────────────────────────── */}
      <section
        className="product-section"
        aria-labelledby="scanner-access-heading"
      >
        <details>
          <summary
            id="scanner-access-heading"
            className="section-title cursor-pointer"
          >
            Scanner access &amp; allowlisting
          </summary>
          <div className="mt-3">
            <p className="text-sm mb-2">
              If your site uses a WAF, bot protection, or login walls, you may
              need to allowlist the AccessiMonitor scanner. Use the details
              below to configure your security rules.
            </p>

            <h4 className="text-sm fw-600 mb-1">
              Accessibility scan requests
            </h4>
            <table className="review-table" style={{ maxWidth: "600px" }}>
              <tbody>
                <tr>
                  <td className="text-sm fw-600">User-Agent</td>
                  <td className="text-sm" style={{ wordBreak: "break-all" }}>
                    Mozilla/5.0 (compatible; AccessiMonitor/1.0;
                    +https://accessimonitor.com/bot) AppleWebKit/537.36 (KHTML,
                    like Gecko) Chrome/120.0.0.0 Safari/537.36
                  </td>
                </tr>
                <tr>
                  <td className="text-sm fw-600">Header</td>
                  <td className="text-sm">X-AccessiMonitor: 1</td>
                </tr>
                <tr>
                  <td className="text-sm fw-600">Header</td>
                  <td className="text-sm">X-AccessiMonitor-Purpose: accessibility-scan</td>
                </tr>
                <tr>
                  <td className="text-sm fw-600">Fixed IP</td>
                  <td className="text-sm text-muted">
                    Not available — contact support if IP-based allowlisting is
                    required
                  </td>
                </tr>
              </tbody>
            </table>

            <ScannerAccessCopyButton />

            <h4 className="text-sm fw-600 mt-3 mb-1">
              Ownership verification requests
            </h4>
            <p className="text-sm text-muted mb-2">
              When you verify site ownership (via HTML meta tag or verification
              file), we make a separate request that identifies itself
              differently from scan traffic. Allowlisting the scanner does not
              affect verification checks.
            </p>
            <table className="review-table" style={{ maxWidth: "600px" }}>
              <tbody>
                <tr>
                  <td className="text-sm fw-600">User-Agent</td>
                  <td className="text-sm" style={{ wordBreak: "break-all" }}>
                    AccessiMonitor-Verify/1.0 (+https://accessimonitor.com/bot)
                  </td>
                </tr>
                <tr>
                  <td className="text-sm fw-600">Header</td>
                  <td className="text-sm">X-AccessiMonitor-Purpose: ownership-verification</td>
                </tr>
              </tbody>
            </table>

            <p className="text-muted text-sm mt-2">
              If pages are still blocked after allowlisting, check your{" "}
              <Link to={`/websites/${websiteId}/report`}>latest report</Link>
              {" "}for detailed per-page diagnostics.
            </p>
          </div>
        </details>
      </section>
    </div>
  );
}

/* ── Scanner access copy button ────────────────────────────────────────────── */

const SCANNER_ACCESS_TEXT = `AccessiMonitor scanner allowlisting details

User-Agent: Mozilla/5.0 (compatible; AccessiMonitor/1.0; +https://accessimonitor.com/bot) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36

Custom headers:
  X-AccessiMonitor: 1
  X-AccessiMonitor-Purpose: accessibility-scan

Fixed IP: Not available. Contact AccessiMonitor support if IP-based allowlisting is required.

Please allowlist automated browser requests matching the User-Agent or custom headers above.`;

function ScannerAccessCopyButton() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(SCANNER_ACCESS_TEXT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API may be unavailable in HTTP contexts
    });
  }

  return (
    <button
      type="button"
      className="btn btn--sm btn--secondary mt-2"
      onClick={handleCopy}
    >
      {copied ? "Copied" : "Copy allowlisting details"}
    </button>
  );
}